import React, { useEffect, Fragment, useState } from "react";
import { oneOfType, array, string, func, bool, arrayOf, shape } from "prop-types";
import { connect, useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { Link } from "react-router-dom";
import { actions as loansActions, selectors as loansSelectors } from "reducers/loans";
import Notification from "pages/_components/Notification";
import MainContainer from "pages/_components/MainContainer";
import Container from "pages/_components/Container";
import I18n from "pages/_components/I18n";
import GridTable from "pages/_components/GridTable/GridTable";
import Button from "pages/_components/Button";
import FormattedAmount from "pages/_components/FormattedAmount";
import * as numberUtils from "util/number";
import CustomProductHead from "pages/_components/CustomProductHead";
import ViewTitle from "pages/_components/header/components/ViewTitle";
import * as configUtils from "util/config";
import MasInformacion from "pages/loans/_components/MasInformacion";
import classNames from "classnames";
import CustomData from "pages/_components/CustomData/CustomData";
import getCellPlacement from "../../util/helpers/table";
import LoansCard from "./_components/LoansCard";

const ApplyForLoan = ({ fetching, loans, isDesktop, qualificationAvailable, rates }) => {
    const isLoading = fetching && !loans.length;
    const [helpIsVisible, setHelpIsVisible] = useState(false);
    const isCustomLoanEnabled = configUtils.getBoolean("loans.customLoan.enabled");

    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(loansActions.unsecuresLoansApplicationPre(""));
        dispatch(loansActions.getRates("ARS"));
    }, []);

    const renderAvailableCredit = () => (
        <GridTable className="summary-table underlined">
            <GridTable.Header>
                <GridTable.Container columns={isDesktop ? 2 : 1} rows={isDesktop ? 1 : 2}>
                    <GridTable.Data
                        columnStart={1}
                        columnWidth={1}
                        rows={isDesktop ? 1 : 4}
                        alignX="flex-start"
                        alignY="center"
                        rowStart={1}>
                        <I18n id="loans.applyForLoan.availableCredit.text" />
                    </GridTable.Data>
                    <GridTable.Data
                        columnStart={isDesktop ? 2 : 1}
                        columnWidth={1}
                        alignX={isDesktop ? "flex-end" : "left"}
                        alignY="center"
                        rowStart={isDesktop ? 1 : 2}>
                        <strong>
                            <FormattedAmount
                                currency="ARS"
                                quantity={numberUtils.formatAmountStrDouble(qualificationAvailable)}
                            />
                        </strong>
                    </GridTable.Data>
                </GridTable.Container>
            </GridTable.Header>
        </GridTable>
    );

    const renderRateInformation = () => (
        <div className="box info">
            <I18n id="loans.applyForLoan.rateInformation" />
        </div>
    );

    const renderCustomLoan = () => {
        const columns = isDesktop ? 8 : 2;
        const rows = isDesktop ? 1 : 3;
        const columnsTemplate = isDesktop ? `repeat(8, 1fr) ` : "1fr";
        const getCellProps = (row, col) => getCellPlacement(row, col, { isDesktop });
        return (
            <>
                <GridTable.Container
                    className="loans__item"
                    columns={columns}
                    rows={rows}
                    renderAs={Link}
                    columnsTemplate={columnsTemplate}
                    to="/requestCustomLoan">
                    <GridTable.Data
                        columnWidth={2}
                        rowWidth={1}
                        className="custom-text-loan-type"
                        alignX="left"
                        alignY="start"
                        flexDirection={isDesktop ? "row" : "column"}
                        {...getCellProps(1, 1)}>
                        <CustomData
                            titleText=" "
                            titleTextType="STRING"
                            valueText="loans.applyForLoan.customLoan.title"
                            valueTextType="I18N"
                            valueClass="custom-text-loan-type font-weight-600"
                        />
                    </GridTable.Data>

                    <GridTable.Data
                        columnWidth={3}
                        rowWidth={1}
                        className="modalities"
                        alignX="left"
                        alignY="start"
                        flexDirection={isDesktop ? "row" : "column"}
                        {...getCellProps(1, 4)}>
                        <I18n id="loans.applyForLoan.customLoan.information" />
                    </GridTable.Data>
                    <GridTable.Data
                        columnWidth={1}
                        rowWidth={1}
                        alignX="left"
                        alignY="start"
                        flexDirection={isDesktop ? "row" : "column"}
                        {...getCellProps(1, 8)}>
                        <Button
                            image="images/figma/icon/arrow-right.svg"
                            bsStyle="link"
                            style={{ flexDirection: "row-reverse", paddingLeft: null, fill: "#172b62" }}
                            label="loans.applyForLoan.customLoan.applyButton.text"
                            onClick={() => {
                                dispatch(push("/requestCustomLoan"));
                            }}
                        />
                    </GridTable.Data>
                </GridTable.Container>
            </>
        );
    };

    const renderHeader = () => {
        const renderMenuOption = () => (
            <>
                <Button
                    className="head__icon-button"
                    bsStyle="link"
                    image="images/figma/icon/info.svg"
                    block={false}
                    style={{ gridColumn: "10 / span 3", padding: 0, marginLeft: "auto" }}
                    label="loans.applyForLoan.header.moreInfo.text"
                    onClick={() => setHelpIsVisible(true)}
                />
                <MasInformacion slideIsVisible={helpIsVisible} closeSlide={() => setHelpIsVisible(false)} />
            </>
        );
        return (
            <CustomProductHead
                titleId="loans.applyForLoan.header.title"
                backLinkTo="/loans"
                addLinkToLabel="loans.applyForLoan.header.title"
                addLinkTo="/applyForLoan"
                className="deposits"
                renderMenuOption={renderMenuOption}>
                <div className="toolbar-item view-title">
                    <ViewTitle id="loans.applyForLoan.header.title" />
                </div>
            </CustomProductHead>
        );
    };

    const loansType = configUtils.getArray(`unsecured.loans`);
    return (
        <Fragment>
            <Notification scopeToShow="loans" />
            {renderHeader({ isDesktop })}
            <MainContainer showLoader={isLoading} className="desktop">
                <div className="above-the-fold loans">
                    <Fragment>
                        <Container className="container--layout pb-8 flex-grow no-scrollable align-items-center">
                            {renderAvailableCredit()}

                            <GridTable className={classNames("products-list")}>
                                {loansType.map((type) => (
                                    <LoansCard
                                        loanType={type}
                                        isDesktop={isDesktop}
                                        availableAmount={qualificationAvailable}
                                        rates={rates}
                                        showSimulateButton={qualificationAvailable > 0}
                                    />
                                ))}
                                {isCustomLoanEnabled && renderCustomLoan()}
                            </GridTable>
                            {renderRateInformation({ isDesktop })}
                        </Container>
                    </Fragment>
                </div>
            </MainContainer>
        </Fragment>
    );
};

ApplyForLoan.propTypes = {
    isRequestAvailable: oneOfType([string, bool]),
    dispatch: func.isRequired,
    isDesktop: oneOfType([string, bool]),
    isMobile: oneOfType([string, bool]),
    fetching: oneOfType([string, bool]),
    loans: oneOfType([array]),
    loansList: oneOfType([array]),
    qualificationAvailable: string.isRequired,
    rates: arrayOf(shape({})).isRequired,
};

ApplyForLoan.defaultProps = {
    isRequestAvailable: false,
    isDesktop: false,
    isMobile: false,
    fetching: false,
    loans: [],
    loansList: [],
};

const mapStateToProps = (state) => ({
    loans: loansSelectors.getLoans(state),
    loansList: loansSelectors.getLoansV2(state),
    fetching: loansSelectors.getFetching(state),
    qualificationAvailable: loansSelectors.getQualificationAvailable(state),
    rates: loansSelectors.getRates(state),
});

export default connect(mapStateToProps)(ApplyForLoan);
